<script>
import InputSelect from '@/components/atoms/inputlib/components/InputSelect'
import InputTextarea from '@/components/atoms/inputlib/components/InputTextarea'
import InputLine from '@/components/atoms/inputlib/components/InputLine'
import Spinner from '@/components/atoms/Spinner'
// import isEmpty from 'lodash.isempty'
import { mapGetters } from 'vuex'
export default {
    name: 'ExpenseCodeRow',
    components: { InputLine, InputTextarea, InputSelect, Spinner },
    inject: {
        expenseCodeRemote: { default: null },
    },
    props: {
        canteen: Boolean,
        labels: Boolean,
        multiple: Boolean,
        isValid: Boolean,
        value: {
            type: Object,
            default: () => {},
        },
        error: {
            type: Object,
            default: () => {},
        },
    },

    data() {
        return {
            // Per-field server search state of the current generation:
            // { query, items, hasMore, total, pending }
            remoteFields: {},
        }
    },

    computed: {
        ...mapGetters(['smDevice', 'lgDevice']),

        blockMode() {
            return this.expenseCodes?.length > 3
        },

        gridCol() {
            return this.blockMode
                ? 'grid-cols-1'
                : this.lgDevice
                ? `grid-cols-${this.expenseCodes?.length || '1'}`
                : `grid-cols-1`
        },

        selectedExpenseRule() {
            return this.canteen
                ? this.$cart.calculation.cloudcanteen
                : this.$cart.calculation.selected_expense_rule
        },

        // Part of the local field keys: another rule's field remounts with its own options
        selectedRuleKey() {
            return this.selectedExpenseRule ? this.selectedExpenseRule.id : 'none'
        },

        expenseCodes() {
            if (!this.selectedExpenseRule)
                return [
                    this.getRule({
                        id: 'default',
                        expense_code_placeholder: this.$t('labels.placeholder.type_code'),
                        title: this.$t('order.expense_code'),
                    }),
                ]
            return this.selectedExpenseRule.additional_expense_codes?.reduce(
                (p, c) => {
                    p.push(this.getRule(c))
                    return p
                },
                [this.getRule({ ...this.selectedExpenseRule, ...{ id: 'default' } })]
            )
        },
        constraints() {
            return JSON.stringify(
                (this.expenseCodes || []).map((expenseCode) => [
                    expenseCode.id,
                    !!expenseCode.required,
                    expenseCode.regex || null,
                ])
            )
        },
        remoteGeneration() {
            return this.expenseCodeRemote ? this.expenseCodeRemote.generation : 0
        },
        remoteLists() {
            return this.expenseCodeRemote?.lists || null
        },
        distribution: {
            get() {
                return this.value.distribution
            },
            set(value) {
                this.$emit('input', {
                    ...this.value,
                    ...{ distribution: Number(value) },
                })
            },
        },
    },

    watch: {
        remoteGeneration() {
            this.cancelRemoteSearches()
            this.remoteFields = {}
        },
        value: {
            immediate: true,
            deep: true,
            handler() {
                this.validateFlag()
            },
        },
        // Required / regex changes under the same fields: the shown errors are refreshed too
        constraints() {
            if (Object.values(this.error || {}).some((error) => error)) this.validateRow()
            else this.validateFlag()
        },
    },

    created() {
        this.remoteTimers = {}
    },

    beforeDestroy() {
        this.remoteDestroyed = true
        this.cancelRemoteSearches()
    },

    methods: {
        isRemote(expenseCode) {
            return this.remoteLists?.[expenseCode.id]?.total > 0
        },
        remoteState(expenseCode) {
            return (
                this.remoteFields[expenseCode.id] || {
                    query: '',
                    ...this.remoteLists?.[expenseCode.id],
                }
            )
        },
        remoteHint(expenseCode) {
            const state = this.remoteState(expenseCode)
            return state.hasMore && !state.pending
                ? this.$t('order.expense_code_has_more', { total: state.total })
                : undefined
        },
        onRemoteSearch(value, expenseCode) {
            const id = expenseCode.id
            const generation = this.remoteGeneration
            const initial = this.remoteLists?.[id]
            clearTimeout(this.remoteTimers[id])
            delete this.remoteTimers[id]
            // An empty search is this generation's initial answer: apply it at once, no request
            if (!(value || '').trim() && initial) {
                this.$set(this.remoteFields, id, {
                    query: value,
                    items: [...initial.items],
                    hasMore: initial.hasMore,
                    total: initial.total,
                    pending: false,
                })
                return
            }
            this.$set(this.remoteFields, id, {
                ...this.remoteState(expenseCode),
                query: value,
                pending: !!(value || '').trim(),
            })
            this.remoteTimers[id] = setTimeout(async () => {
                delete this.remoteTimers[id]
                const result = await this.expenseCodeRemote.searchFor(id, value)
                const state = this.remoteFields[id]
                // Only the answer to the field's latest query of the current context is applied
                if (this.remoteDestroyed || generation !== this.remoteGeneration) return
                if (!state || state.query !== value) return
                this.$set(
                    this.remoteFields,
                    id,
                    result
                        ? {
                              query: value,
                              items: [...result.items],
                              hasMore: result.hasMore,
                              total: result.total,
                              pending: false,
                          }
                        : { ...state, items: [], hasMore: false, pending: false }
                )
            }, 300)
        },
        cancelRemoteSearches() {
            Object.values(this.remoteTimers).forEach((timer) => clearTimeout(timer))
            this.remoteTimers = {}
        },
        deleteRow() {
            this.$emit('delete')
        },
        getRule(rule) {
            return {
                list: rule?.expense_code_list || rule?.available_expense_codes || [],
                placeholder: rule?.expense_code_placeholder,
                regex: rule?.expense_code_regex_validation,
                validationError: rule?.expense_code_regex_validation_error,
                id: rule?.id,
                required: rule?.expense_code_required === 2 || rule?.is_required,
                listOnly: rule?.expense_code_only_from_list || rule?.only_from_list,
                title: rule?.expense_code_title || rule?.title,
            }
        },
        setValue(value, expenseCode) {
            this.$emit('input', {
                ...this.value,
                ...{ codes: { ...this.value.codes, ...{ [expenseCode.id]: value } } },
            })
        },
        validateField(value, expenseCode) {
            if (!value?.length) {
                this.$emit('update:error', {
                    ...this.error,
                    ...{
                        [expenseCode.id]: expenseCode.required
                            ? this.$t('labels.error.field_required')
                            : null,
                    },
                })
            } else if (expenseCode.regex) {
                const regex = new RegExp(expenseCode.regex, 'g')
                this.$emit('update:error', {
                    ...this.error,
                    ...{
                        [expenseCode.id]: regex.test(value) ? null : expenseCode.validationError,
                    },
                })
            } else if (this.error[expenseCode.id]) {
                this.$emit('update:error', {
                    ...this.error,
                    ...{ [expenseCode.id]: null },
                })
            }
        },
        validateRow() {
            const error = {}
            const isValid = this.expenseCodes.reduce((isValid, expenseCode) => {
                if (!this.value.codes[expenseCode.id]) {
                    error[expenseCode.id] = expenseCode.required
                        ? this.$t('labels.error.field_required')
                        : null
                } else if (expenseCode.regex) {
                    const regex = new RegExp(expenseCode.regex, 'g')
                    error[expenseCode.id] = regex.test(this.value.codes[expenseCode.id])
                        ? null
                        : expenseCode.validationError
                } else {
                    error[expenseCode.id] = null
                }
                if (isValid) isValid = !error[expenseCode.id]
                return isValid
            }, true)
            this.$emit('update:isValid', isValid)
            this.$emit('update:error', error)
            return isValid
        },
        validateFlag() {
            this.$emit(
                'update:isValid',
                !this.expenseCodes
                    .map((expenseCode) => {
                        if (expenseCode.required && !this.value.codes[expenseCode.id]) return false
                        if (expenseCode.regex && this.value.codes[expenseCode.id]) {
                            const regex = new RegExp(expenseCode.regex, 'g')

                            return !!regex.test(this.value.codes[expenseCode.id])
                        }
                        return true
                    }, true)
                    ?.includes(false)
            )
        },
    },
}
</script>

<template lang="pug">
.expense-code-row
    hr(v-if="!labels && (blockMode || smDevice)")
    .grid(:class="[smDevice ? 'gap-6' : 'gap-3', {'multiple-code': !blockMode && multiple && lgDevice}]")
        .grid(:class="[smDevice ? 'gap-6' : 'gap-3', gridCol]")
            div(v-if="value && value.codes" v-for="(expenseCode, index) in expenseCodes")
                input-select.w-100(
                    v-if="isRemote(expenseCode)"
                    :key="`expenseCode-${_uid}-${index}-remote-${remoteGeneration}`"
                    :value="value.codes[expenseCode.id]"
                    @input="setValue($event, expenseCode)"
                    @close="validateField($event, expenseCode)"
                    @search="onRemoteSearch($event, expenseCode)"
                    :placeholder="expenseCode.placeholder"
                    :error="error[expenseCode.id]"
                    :required="expenseCode.required"
                    :no-empty="expenseCode.required"
                    :clearable="!expenseCode.required"
                    :options="remoteState(expenseCode).items"
                    :after-list-text="remoteHint(expenseCode)"
                    :loading="!!remoteState(expenseCode).pending"
                    :searchable="expenseCode.listOnly"
                    :combo="!expenseCode.listOnly"
                    remote)
                    template(v-if="labels || smDevice || blockMode")
                        span.text-ellipsis {{ expenseCode.title }}
                input-textarea.mb-0.w-100(
                    v-else-if="!expenseCode.listOnly && !expenseCode.list.length" 
                    :key="`expenseCode-${_uid}-${selectedRuleKey}-${expenseCode.id}`" 
                    :value="value.codes[expenseCode.id]" 
                    @input="setValue($event, expenseCode)"
                    @change="validateField($event, expenseCode)"
                    @enter="validateField($event, expenseCode)"
                    :placeholder="expenseCode.placeholder"
                    :error="error[expenseCode.id]"
                    :max="null"
                    :min-height="40"
                    resize
                    :required="expenseCode.required")
                    template(v-if="labels || smDevice || blockMode") 
                        span.text-ellipsis {{ expenseCode.title }}
                input-select.w-100(
                    v-else 
                    :key="`expenseCode-${_uid}-${selectedRuleKey}-${expenseCode.id}-local`" 
                    :value="value.codes[expenseCode.id]"
                    @input="setValue($event, expenseCode)"
                    @close="validateField($event, expenseCode)"
                    :placeholder="expenseCode.placeholder" 
                    :error="error[expenseCode.id]"
                    :required="expenseCode.required"
                    :no-empty="expenseCode.required"
                    :clearable="!expenseCode.required"
                    :options="expenseCode.list"
                    :searchable="expenseCode.listOnly"
                    :combo="!expenseCode.listOnly") 
                    template(v-if="labels || smDevice || blockMode")
                        span.text-ellipsis {{ expenseCode.title }}
        .d-flex.align-items-start.gap-2(v-if="multiple")
            input-line.mb-0.w-100.text-right(                
                v-model="distribution"
                align-right 
                type="number" 
                hide-step)
                template(v-if="labels || smDevice || blockMode") 
                    span.text-ellipsis {{ $t('labels.allocation') }}
            button.btn.btn-transparent.btn-icon(@click="deleteRow" :class="{'mt-delete': labels}")
                icon(icon="fa-solid fa-trash")

 
</template>

<style scoped>
.expense-code-row >>> .multiple-code {
    grid-template-columns: minmax(0, 1fr) 129px;
}
.expense-code-row >>> .mt-delete {
    margin-top: 19px;
}
</style>
